/**
 * reports/hooks/useReports.js
 *
 * Listing + per-report fetch + run + mutations, all in one file so pages
 * import a single set of hooks. The shape mirrors React Query — `data`,
 * `isLoading`, `refetch`, `mutate` — so swapping the shim later is trivial.
 */
import {
  useQuery, useMutation, invalidateQueries,
  getQueryData, setQueryData,
} from '../../analytics-shared/hooks/queryShim';
import { reportsApi } from '../api/reportsApi';

const LIST_KEY = ['reports', 'list'];

export function useReportsList() {
  const q = useQuery({
    queryKey: ['reports', 'list'],
    queryFn: () => reportsApi.list(),
    // Tighter than the old 15s so favorite/folder/visibility mutations
    // (which call invalidateQueries(['reports'])) show up in the list
    // immediately. Combined with the queryShim fix that makes
    // invalidate actually refetch, the sidebar/counters now stay in
    // sync across tabs.
    staleTime: 5_000,
  });
  return { ...q, reports: q.data || [] };
}

export function useReport(id) {
  return useQuery({
    queryKey: ['reports', 'detail', id],
    queryFn: () => reportsApi.get(id),
    enabled: !!id,
    staleTime: 5_000,
  });
}

export function useReportRun(config) {
  return useQuery({
    queryKey: ['reports', 'run', config?.id, config?.updatedAt],
    queryFn: () => reportsApi.run(config),
    enabled: !!config,
    // Salesforce-style: each fresh page entry / window focus shows
    // the latest CRM data without forcing a manual Run. Keep a tiny
    // staleTime so successive mounts within a few seconds share a
    // result (e.g. tab focus + visibility change racing each other).
    staleTime: 2_000,
  });
}

export function useReportMutations() {
  const save = useMutation({
    mutationFn: (report) => reportsApi.save(report),
    onSuccess: () => invalidateQueries(['reports']),
  });
  // Optimistic delete: yank the row out of the list cache the moment
  // the click registers. The mutation still runs server-side and the
  // post-mutation invalidate confirms the server agreed, but the user
  // sees the card disappear in the same frame as the click. If the
  // delete fails we'd want a rollback — we trade that off for the
  // snappier UX (the backend is the source of truth on next refetch).
  const remove = useMutation({
    mutationFn: (id) => reportsApi.remove(id),
    onMutate: (id) => {
      const prev = getQueryData(LIST_KEY);
      if (Array.isArray(prev)) {
        setQueryData(LIST_KEY, prev.filter((r) => r.id !== id));
      }
      return { prev };
    },
    onSuccess: () => invalidateQueries(['reports']),
  });
  const duplicate = useMutation({
    mutationFn: (id) => reportsApi.duplicate(id),
    onSuccess: () => invalidateQueries(['reports']),
  });
  // Optimistic favorite flip — same idea as delete, faster than a
  // round trip and the server-side refresh confirms.
  const toggleFavorite = useMutation({
    mutationFn: (id) => reportsApi.toggleFavorite(id),
    onMutate: (id) => {
      const prev = getQueryData(LIST_KEY);
      if (Array.isArray(prev)) {
        setQueryData(LIST_KEY, prev.map((r) => (
          r.id === id ? { ...r, favorite: !r.favorite } : r
        )));
      }
      return { prev };
    },
    onSuccess: () => invalidateQueries(['reports']),
  });
  const markRun = useMutation({
    mutationFn: (id) => reportsApi.markRun(id),
    onSuccess: () => invalidateQueries(['reports']),
  });
  return { save, remove, duplicate, toggleFavorite, markRun };
}
